import { Preferences } from '@capacitor/preferences'

export const ROAST_RECENCY_STORAGE_KEY = 'tabofshame.roast-recency.v1'
export const ROAST_RECENCY_WINDOW_MS = 14 * 24 * 60 * 60 * 1000
export const ROAST_RECENCY_MAX_ENTRIES = 400
export const ROAST_RECENCY_MIN_FRESH_FRACTION = 0.15

type RecencyStore = { hashes: Record<string, number> }

let memoryStore: RecencyStore | null = null

function readStore(): RecencyStore {
  if (memoryStore) return memoryStore
  memoryStore = { hashes: {} }
  return memoryStore
}

async function loadStore(): Promise<RecencyStore> {
  if (memoryStore) return memoryStore
  try {
    const { value } = await Preferences.get({ key: ROAST_RECENCY_STORAGE_KEY })
    if (value) {
      const parsed = JSON.parse(value) as RecencyStore
      if (parsed?.hashes && typeof parsed.hashes === 'object') {
        memoryStore = { hashes: { ...parsed.hashes } }
        return memoryStore
      }
    }
  } catch {
    /* ignore */
  }
  memoryStore = { hashes: {} }
  return memoryStore
}

async function persistStore(store: RecencyStore): Promise<void> {
  memoryStore = store
  try {
    await Preferences.set({ key: ROAST_RECENCY_STORAGE_KEY, value: JSON.stringify(store) })
  } catch {
    /* ignore */
  }
}

function pruneStore(store: RecencyStore, now: number): void {
  const cutoff = now - ROAST_RECENCY_WINDOW_MS
  for (const [h, ts] of Object.entries(store.hashes)) {
    if (!Number.isFinite(ts) || ts < cutoff) delete store.hashes[h]
  }
  const entries = Object.entries(store.hashes)
  if (entries.length <= ROAST_RECENCY_MAX_ENTRIES) return
  entries.sort((a, b) => a[1] - b[1])
  const drop = entries.length - ROAST_RECENCY_MAX_ENTRIES
  for (let i = 0; i < drop; i += 1) delete store.hashes[entries[i][0]]
}

export function hashRoastLine(text: string): string {
  const s = String(text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!s) return ''
  let h = 2166136261
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0).toString(36)
}

export async function isRecentlySpokenRoast(text: string, now = Date.now()): Promise<boolean> {
  const h = hashRoastLine(text)
  if (!h) return false
  const store = await loadStore()
  const ts = store.hashes[h]
  if (!Number.isFinite(ts)) return false
  return now - ts < ROAST_RECENCY_WINDOW_MS
}

export async function recordSpokenRoast(text: string, now = Date.now()): Promise<void> {
  const h = hashRoastLine(text)
  if (!h) return
  const store = await loadStore()
  store.hashes[h] = now
  pruneStore(store, now)
  await persistStore(store)
}

export function preferFreshRoastIndices(
  arr: readonly string[],
  candidateIndices: readonly number[],
  recentHashes: ReadonlySet<string>,
): number[] {
  if (candidateIndices.length === 0) return []
  const fresh = candidateIndices.filter((i) => {
    const line = arr[i]
    if (typeof line !== 'string') return false
    const h = hashRoastLine(line)
    return h && !recentHashes.has(h)
  })
  const minFresh = Math.max(1, Math.ceil(candidateIndices.length * ROAST_RECENCY_MIN_FRESH_FRACTION))
  if (fresh.length >= minFresh) return fresh
  return [...candidateIndices]
}

export async function recentRoastHashSet(now = Date.now()): Promise<Set<string>> {
  const store = await loadStore()
  const cutoff = now - ROAST_RECENCY_WINDOW_MS
  const out = new Set<string>()
  for (const [h, ts] of Object.entries(store.hashes)) {
    if (Number.isFinite(ts) && now - ts < cutoff) out.add(h)
  }
  return out
}
